/**
 * Add Noise — procedural per-channel noise (Gaussian or uniform).
 *
 * Adds independent random noise to each RGB channel. Used creatively
 * (film-grain pastiche) or as a regularising step before sharpening
 * (gives Richardson-Lucy / USM something non-zero to amplify on flat
 * regions, suppressing colour banding).
 *
 * Distributions:
 *   • 'uniform'  → uniform on [−amount, +amount]
 *   • 'gaussian' → standard normal × amount (via Box-Muller transform)
 *
 * RNG: xorshift32. Cheap, deterministic from a 32-bit seed, decent
 * statistical properties for image noise (NOT cryptographically
 * secure — we don't need that). Same RNG implementation is reused
 * across the editor so deterministic-output tests work.
 *
 * Pipeline slot: destructive pre-kernel commit. Identity short-circuit:
 * amount ≤ 0 → no-op.
 *
 * References:
 *   • Marsaglia, G. (2003) "Xorshift RNGs", J. Statistical Software.
 *   • Box, G.E.P. & Muller, M.E. (1958) "A Note on the Generation of
 *     Random Normal Deviates", Annals of Mathematical Statistics.
 *
 * License: BLRRD is MIT. NO GPLv3 source consulted.
 *
 * @module src/pipeline/filters/spatial/noise
 */

import { validateRGBA } from './_util.js';

/** Slider range. amount=0 is identity; amount=1 is "very noisy". */
export const NOISE_AMOUNT_MIN = 0;
export const NOISE_AMOUNT_MAX = 1;

const IDENTITY_EPS = 1e-6;

/* Default seed used when caller doesn't supply one. Fixed value gives
 * reproducible CI runs. */
const DEFAULT_SEED = 0x9E3779B1;

/**
 * Default Noise parameters. Identity (amount = 0).
 *
 * @returns {{ amount: number, distribution: 'gaussian'|'uniform', seed: number }}
 */
export function defaultParams() {
    return { amount: 0, distribution: 'gaussian', seed: DEFAULT_SEED };
}

/**
 * Identity test.
 *
 * @param {{ amount?: number }} params
 * @returns {boolean}
 */
export function isIdentityNoise(params) {
    if (!params) return true;
    const a = +params.amount;
    if (!Number.isFinite(a)) return true;
    return a <= IDENTITY_EPS;
}

/**
 * xorshift32 PRNG. Returns a uint32 each call; the caller maps to
 * the desired distribution. Deterministic from a non-zero seed.
 *
 * @param {number} seed   any non-zero 32-bit unsigned int
 * @returns {() => number}  call to get next uint32
 */
export function makeXorshift32(seed) {
    let s = (seed | 0) || 1;
    return function next() {
        s ^= s << 13;
        s ^= s >>> 17;
        s ^= s << 5;
        return s >>> 0;
    };
}

/**
 * Apply Add Noise in-place on a packed RGBA Float32 buffer.
 *
 * Each channel of each pixel gets an independent draw from the chosen
 * distribution. Output is NOT clamped — scene-linear ACEScg can carry
 * negative spill (and the downstream filmic view transform handles
 * the LDR clamp).
 *
 * @param {Float32Array} rgba    scene-linear RGBA, length 4·w·h
 * @param {number} w
 * @param {number} h
 * @param {object} params
 * @param {number} params.amount       [0, 1]
 * @param {'gaussian'|'uniform'} [params.distribution='gaussian']
 * @param {number} [params.seed]       xorshift32 seed (non-zero)
 * @returns {Float32Array}       same `rgba` reference (in-place)
 */
export function applyAddNoise(rgba, w, h, params) {
    validateRGBA('applyAddNoise', rgba, w, h);
    const p = params || {};
    if (isIdentityNoise(p)) return rgba;

    const amount = Math.min(NOISE_AMOUNT_MAX, Math.max(NOISE_AMOUNT_MIN, +p.amount));
    const distribution = p.distribution === 'uniform' ? 'uniform' : 'gaussian';
    const seed = Number.isFinite(p.seed) ? (p.seed | 0) : DEFAULT_SEED;
    const rand = makeXorshift32(seed || 1);

    const N = w * h;
    if (distribution === 'uniform') {
        /* u in [0, 1) → 2·u−1 in [−1, 1) → ·amount. */
        for (let i = 0, j = 0; i < N; i++, j += 4) {
            for (let c = 0; c < 3; c++) {
                const u = (rand() & 0xFFFFFF) / 0x1000000;
                rgba[j + c] += (u * 2 - 1) * amount;
            }
        }
        return rgba;
    }
    /* Gaussian via Box-Muller. Produces TWO normals per pair of
     * uniforms, so we cache the second one in `spare` for the next
     * call. The cache lives across the inner loop but is local —
     * NOT module-state, so two threads can each call this fn safely. */
    let hasSpare = false;
    let spare = 0;
    function gauss() {
        if (hasSpare) { hasSpare = false; return spare; }
        let u1, u2;
        do {
            u1 = (rand() & 0xFFFFFF) / 0x1000000;
        } while (u1 <= 1e-12);
        u2 = (rand() & 0xFFFFFF) / 0x1000000;
        const mag = Math.sqrt(-2 * Math.log(u1));
        const z0 = mag * Math.cos(2 * Math.PI * u2);
        const z1 = mag * Math.sin(2 * Math.PI * u2);
        spare = z1;
        hasSpare = true;
        return z0;
    }
    for (let i = 0, j = 0; i < N; i++, j += 4) {
        rgba[j]     += gauss() * amount;
        rgba[j + 1] += gauss() * amount;
        rgba[j + 2] += gauss() * amount;
        /* alpha untouched. */
    }
    return rgba;
}
